import {
  escapeHtml,
  getConfig,
  getUserToken,
  highlight,
  highlightParams,
  imageUrl,
  moneyFormatter,
  multiQuery,
  sendInsight,
  toParams,
} from '@theme/pa-algolia';

const RECENT_KEY = 'pa-recent-searches';
const RECENT_LIMIT = 5;
const CACHE_LIMIT = 50;
const PANEL_GAP = 6;
const MIN_PANEL_WIDTH = 560;

const decodeEntities = (value) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

function readRecent() {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_LIMIT)));
  } catch {}
}

export function rememberSearch(query) {
  const term = query.trim();
  if (!term) return;
  writeRecent([term, ...readRecent().filter((item) => item.toLowerCase() !== term.toLowerCase())]);
}

class Autocomplete {
  /** @type {AbortController | null} */
  #controller = null;
  #cache = new Map();
  #requestId = 0;
  #activeIndex = -1;
  #query = '';
  #results = null;
  #open = false;

  /**
   * @param {HTMLInputElement} input
   * @param {import('./pa-algolia.js').AlgoliaConfig & { autocomplete: any, searchUrl: string, collectionsUrl: string }} config
   * @param {Record<string, string>} icons
   */
  constructor(input, config, icons) {
    this.input = input;
    this.form = input.form;
    this.config = config;
    this.strings = config.autocomplete.strings;
    this.icons = icons;
    this.money = moneyFormatter(config);
    this.number = new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en');
    this.anchor = this.form?.closest('pa-header') ?? document.body;
    this.id = `pa-ac-${Math.random().toString(36).slice(2, 8)}`;

    this.panel = document.createElement('div');
    this.panel.className = 'pa-ac';
    this.panel.id = this.id;
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'listbox');
    this.panel.setAttribute('aria-label', this.strings.label);
    this.anchor.append(this.panel);

    this.status = document.createElement('div');
    this.status.className = 'pa-ac__status';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.anchor.append(this.status);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', this.id);
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');

    input.addEventListener('input', this.#onInput);
    input.addEventListener('focus', this.#onFocus);
    input.addEventListener('keydown', this.#onKeydown);
    this.form?.addEventListener('submit', this.#onSubmit);
    this.form?.addEventListener('focusout', this.#onFocusOut);
    this.panel.addEventListener('focusout', this.#onFocusOut);
    this.panel.addEventListener('mousedown', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('button')) event.preventDefault();
    });
    this.panel.addEventListener('mousemove', this.#onPointerMove);
    this.panel.addEventListener('click', this.#onPanelClick);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    window.addEventListener('resize', () => this.#open && this.#position());
  }

  /* ---------- Data ---------- */

  #buildRequests(query) {
    const { productsIndex, collectionsIndex, autocomplete } = this.config;
    const requests = [
      {
        indexName: productsIndex,
        params: toParams({
          query,
          hitsPerPage: String(autocomplete.products),
          attributesToRetrieve: ['title', 'handle', 'vendor', 'product_type', 'images', 'image', 'variants_min_price', 'variants_max_price', 'tags'],
          attributesToHighlight: ['title'],
          attributesToSnippet: [],
          ...highlightParams,
          clickAnalytics: 'true',
          userToken: getUserToken(),
          analyticsTags: ['theme-autocomplete'],
        }),
      },
    ];

    if (collectionsIndex && autocomplete.collections > 0) {
      requests.push({
        indexName: collectionsIndex,
        params: toParams({
          query,
          hitsPerPage: String(autocomplete.collections),
          attributesToRetrieve: ['title', 'handle', 'products_count', 'body_html_safe'],
          attributesToHighlight: ['title'],
          attributesToSnippet: [],
          removeWordsIfNoResults: 'none',
          typoTolerance: 'min',
          ...highlightParams,
          clickAnalytics: 'true',
          userToken: getUserToken(),
          analyticsTags: ['theme-autocomplete'],
        }),
      });
    }

    return requests;
  }

  async #fetch(query) {
    const key = query.trim().toLowerCase();
    if (this.#cache.has(key)) return this.#cache.get(key);

    this.#controller?.abort();
    this.#controller = new AbortController();
    const data = await multiQuery(this.config, this.#buildRequests(query), { signal: this.#controller.signal });
    const [products, collections] = data.results;
    const result = { products, collections: collections ?? null };

    this.#cache.set(key, result);
    if (this.#cache.size > CACHE_LIMIT) this.#cache.delete(this.#cache.keys().next().value);
    return result;
  }

  async #update() {
    const query = this.input.value;
    this.#query = query;
    const requestId = ++this.#requestId;

    if (!query.trim()) {
      this.#results = null;
      this.#renderRecent();
      return;
    }

    try {
      const result = await this.#fetch(query);
      if (requestId !== this.#requestId) return;
      this.#results = result;
      this.#renderResults(query, result);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[pa-algolia-autocomplete]', error);
      if (requestId === this.#requestId) this.close();
    }
  }

  /* ---------- Rendering ---------- */

  #productUrl(hit) {
    return `/products/${hit.handle}`;
  }

  #searchUrl(query) {
    const url = new URL(this.config.searchUrl, window.location.origin);
    url.searchParams.set('q', query);
    return url.pathname + url.search;
  }

  #option(id, inner, { href, className = '', data = {}, after = '' } = {}) {
    const attrs = Object.entries(data)
      .map(([key, value]) => ` data-${key}="${escapeHtml(value)}"`)
      .join('');
    return `<li role="presentation" class="pa-ac__item"><a id="${id}" role="option" aria-selected="false" class="pa-ac__option ${className}" href="${escapeHtml(href)}" tabindex="-1"${attrs}>${inner}</a>${after}</li>`;
  }

  #renderRecent() {
    const recent = readRecent();
    if (!recent.length) {
      this.close();
      return;
    }

    const items = recent
      .map((term, i) =>
        this.#option(
          `${this.id}-r${i}`,
          `<span class="pa-ac__option-icon" aria-hidden="true">${this.icons.recent}</span>
           <span class="pa-ac__text"><span class="pa-ac__title">${escapeHtml(term)}</span></span>`,
          {
            href: this.#searchUrl(term),
            data: { kind: 'recent', query: term },
            after: `<button type="button" class="pa-ac__remove" data-remove-recent="${escapeHtml(term)}" aria-label="${escapeHtml(this.strings.removeRecent.replace('[term]', term))}">${this.icons.close}</button>`,
          }
        )
      )
      .join('');

    this.panel.innerHTML = `<div class="pa-ac__body">
      <div class="pa-ac__column">
        <div class="pa-ac__group" role="group" aria-labelledby="${this.id}-recent">
          <p class="pa-ac__heading" id="${this.id}-recent">${escapeHtml(this.strings.recent)}
            <button type="button" class="pa-ac__heading-action" data-clear-recent>${escapeHtml(this.strings.clearRecent)}</button>
          </p>
          <ul class="pa-ac__list" role="presentation">${items}</ul>
        </div>
      </div>
    </div>`;
    this.status.textContent = '';
    this.#show();
  }

  #renderResults(query, { products, collections }) {
    const { strings } = this;
    const productHits = products?.hits ?? [];
    const collectionHits = collections?.hits ?? [];

    if (!productHits.length && !collectionHits.length) {
      this.panel.innerHTML = `<div class="pa-ac__empty">
        <p class="pa-ac__empty-title">${escapeHtml(strings.noResults.replace('[terms]', query))}</p>
        <p class="pa-ac__empty-text">${escapeHtml(strings.noResultsHint)}</p>
      </div>`;
      this.status.textContent = strings.noResults.replace('[terms]', query);
      this.#show();
      return;
    }

    const productItems = productHits
      .map((hit, i) => {
        const image = hit.images?.[0]?.url ?? hit.image ?? '';
        const min = hit.variants_min_price ?? 0;
        const max = hit.variants_max_price ?? min;
        const isFrom = max > min || (hit.tags ?? []).includes('c3-configurable');
        const price = isFrom ? strings.priceFrom.replace('[price]', this.money(min)) : this.money(min);
        const meta = [
          hit.vendor ? `<span class="pa-ac__vendor">${escapeHtml(hit.vendor)}</span>` : '',
          hit.product_type ? escapeHtml(hit.product_type) : '',
        ]
          .filter(Boolean)
          .join(' · ');

        return this.#option(
          `${this.id}-p${i}`,
          `<span class="pa-ac__thumb" aria-hidden="true">${image ? `<img src="${escapeHtml(imageUrl(image, 120))}" alt="" width="56" height="56">` : this.icons.category}</span>
           <span class="pa-ac__text">
             <span class="pa-ac__title">${highlight(hit._highlightResult?.title?.value ?? hit.title)}</span>
             ${meta ? `<span class="pa-ac__meta">${meta}</span>` : ''}
           </span>
           ${min > 0 ? `<span class="pa-ac__price">${escapeHtml(price)}</span>` : ''}`,
          {
            href: this.#productUrl(hit),
            data: { kind: 'product', index: products.index, objectId: hit.objectID, position: i + 1, queryId: products.queryID ?? '' },
          }
        );
      })
      .join('');

    const collectionItems = collectionHits
      .map((hit, i) => {
        const breadcrumb = decodeEntities(String(hit.body_html_safe ?? '')).trim();
        const count = Number(hit.products_count) || 0;
        const countLabel = (count === 1 ? strings.productsOne : strings.productsOther).replace('[count]', this.number.format(count));
        const parent = breadcrumb.includes('>') ? breadcrumb.split('>').slice(0, -1).join(' › ').trim() : '';
        const meta = [parent, count ? countLabel : ''].filter(Boolean).join(' · ');

        return this.#option(
          `${this.id}-c${i}`,
          `<span class="pa-ac__option-icon" aria-hidden="true">${this.icons.category}</span>
           <span class="pa-ac__text">
             <span class="pa-ac__title">${highlight(hit._highlightResult?.title?.value ?? hit.title)}</span>
             ${meta ? `<span class="pa-ac__meta">${escapeHtml(meta)}</span>` : ''}
           </span>`,
          {
            href: `${this.config.collectionsUrl}/${hit.handle}`,
            data: { kind: 'collection', index: collections.index, objectId: hit.objectID, position: i + 1, queryId: collections.queryID ?? '' },
          }
        );
      })
      .join('');

    const aside = collectionItems
      ? `<div class="pa-ac__column pa-ac__column--aside">
          <div class="pa-ac__group" role="group" aria-labelledby="${this.id}-cats">
            <p class="pa-ac__heading" id="${this.id}-cats">${escapeHtml(strings.categories)}</p>
            <ul class="pa-ac__list" role="presentation">${collectionItems}</ul>
          </div>
        </div>`
      : '';

    const main = productItems
      ? `<div class="pa-ac__column">
          <div class="pa-ac__group" role="group" aria-labelledby="${this.id}-products">
            <p class="pa-ac__heading" id="${this.id}-products">${escapeHtml(strings.products)}</p>
            <ul class="pa-ac__list" role="presentation">${productItems}</ul>
          </div>
        </div>`
      : '';

    const total = products?.nbHits ?? 0;
    const seeAll = total
      ? `<div class="pa-ac__footer" role="presentation">
          <a id="${this.id}-all" role="option" aria-selected="false" tabindex="-1" class="pa-ac__see-all" href="${escapeHtml(this.#searchUrl(query))}" data-kind="all" data-query="${escapeHtml(query)}">
            <span>${escapeHtml(strings.seeAll.replace('[count]', this.number.format(total)))}</span>
            <span aria-hidden="true">${this.icons.arrow}</span>
          </a>
        </div>`
      : '';

    this.panel.innerHTML = `<div class="pa-ac__body${aside && main ? ' pa-ac__body--split' : ''}">${aside}${main}</div>${seeAll}`;
    this.status.textContent = strings.seeAll.replace('[count]', this.number.format(total));
    this.#show();
  }

  /* ---------- Open / close / position ---------- */

  #position() {
    const anchorRect = this.anchor.getBoundingClientRect();
    const formRect = (this.form ?? this.input).getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;
    const margin = 8;
    const width = Math.min(Math.max(formRect.width, MIN_PANEL_WIDTH), viewport - margin * 2);
    const left = Math.min(Math.max(formRect.left, margin), viewport - width - margin);
    const top = formRect.bottom + PANEL_GAP;

    this.panel.style.top = `${top - anchorRect.top}px`;
    this.panel.style.left = `${left - anchorRect.left}px`;
    this.panel.style.width = `${width}px`;
    this.panel.style.setProperty('--pa-ac-top', `${top}px`);
  }

  #show() {
    this.#activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.#position();
    if (this.#open) return;
    this.#open = true;
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.panel.toggleAttribute('data-visible', true));
  }

  close() {
    if (!this.#open) return;
    this.#open = false;
    this.#requestId++;
    this.#controller?.abort();
    this.panel.hidden = true;
    this.panel.removeAttribute('data-visible');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.#activeIndex = -1;
  }

  /* ---------- Keyboard & pointer ---------- */

  get #options() {
    return [...this.panel.querySelectorAll('[role="option"]')];
  }

  #setActive(index) {
    const options = this.#options;
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.#activeIndex = index;
    const active = options[index];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  #onInput = () => {
    this.#update();
  };

  #onFocus = () => {
    if (this.#open) return;
    this.#update();
  };

  #onKeydown = (event) => {
    if (event.isComposing) return;
    const options = this.#options;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!this.#open) {
          this.#update();
          return;
        }
        if (options.length) this.#setActive((this.#activeIndex + 1) % options.length);
        break;
      }
      case 'ArrowUp': {
        if (!this.#open || !options.length) return;
        event.preventDefault();
        this.#setActive(this.#activeIndex <= 0 ? options.length - 1 : this.#activeIndex - 1);
        break;
      }
      case 'Enter': {
        const active = options[this.#activeIndex];
        if (!this.#open || !active) return;
        event.preventDefault();
        this.#select(active, event);
        break;
      }
      case 'Escape': {
        if (!this.#open) return;
        event.preventDefault();
        this.close();
        break;
      }
      case 'Tab':
        this.close();
        break;
    }
  };

  #onPointerMove = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
    if (!option) return;
    const index = this.#options.indexOf(option);
    if (index !== this.#activeIndex) this.#setActive(index);
  };

  #onPanelClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const remove = target.closest('[data-remove-recent]');
    if (remove) {
      event.preventDefault();
      event.stopPropagation();
      writeRecent(readRecent().filter((item) => item !== remove.dataset.removeRecent));
      this.input.focus();
      this.#renderRecent();
      return;
    }

    if (target.closest('[data-clear-recent]')) {
      event.preventDefault();
      writeRecent([]);
      this.input.focus();
      this.close();
      return;
    }

    const option = target.closest('[role="option"]');
    if (option) this.#select(option, event, { navigate: false });
  };

  /**
   * @param {HTMLAnchorElement} option
   * @param {Event} event
   * @param {{ navigate?: boolean }} [options]
   */
  #select(option, event, { navigate = true } = {}) {
    const { kind, query } = option.dataset;

    if (kind === 'recent' || kind === 'all') {
      rememberSearch(query ?? '');
    } else {
      rememberSearch(this.#query);
      sendInsight(this.config, {
        eventType: 'click',
        eventName: kind === 'collection' ? 'Category Clicked' : 'Product Clicked',
        index: option.dataset.index,
        queryID: option.dataset.queryId,
        objectIDs: [option.dataset.objectId],
        positions: [Number(option.dataset.position)],
      });
    }

    if (kind === 'recent') this.input.value = query ?? '';

    const opensNewTab = event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);
    if (!opensNewTab) this.close();
    if (navigate) window.location.assign(option.href);
  }

  #onSubmit = () => {
    rememberSearch(this.input.value);
    this.close();
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && (this.form?.contains(next) || this.panel.contains(next))) return;
    window.setTimeout(() => {
      const active = document.activeElement;
      if (active && (this.form?.contains(active) || this.panel.contains(active))) return;
      this.close();
    }, 0);
  };

  #onDocumentPointerDown = (event) => {
    if (!this.#open) return;
    const target = event.target;
    if (target instanceof Node && (this.form?.contains(target) || this.panel.contains(target))) return;
    this.close();
  };
}

function init() {
  const config = getConfig();
  const template = document.getElementById('pa-ac-icons');
  if (!config || !(template instanceof HTMLTemplateElement)) return;

  const icons = Object.fromEntries(
    [...template.content.querySelectorAll('[data-icon]')].map((el) => [el.dataset.icon, el.innerHTML])
  );

  for (const input of document.querySelectorAll('.pa-header__search-input')) {
    if (!(input instanceof HTMLInputElement) || input.dataset.paAutocomplete) continue;
    input.dataset.paAutocomplete = 'true';
    new Autocomplete(input, config, icons);
  }

  const pageInput = document.querySelector('.search-page__header input[name="q"]');
  pageInput?.form?.addEventListener('submit', () => rememberSearch(pageInput.value));
}

init();
